import { DEMO_FIELD_TYPE, DEMO_CALCULATOR_STEP } from 'config/constants'
import { centerDemoField } from './center-field'
import {
  DEMO_CHARACTER_DELAY,
  DEMO_SCROLL_DELAY,
  DEMO_STEP_DELAY,
} from './playback-constants'
import {
  animateAction,
  prepareField,
  refreshFieldOverlayStyle,
  restoreFields,
  scrollToDemoPlayerElement,
  wait,
  waitForDocumentElement,
} from './helpers'

const waitForFieldLayout = () => new Promise<void>(resolve => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => resolve())
  })
})

const getInput = (element: HTMLElement) => {
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    return element
  }

  return element.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
}

const setInputValue = (
  input: HTMLInputElement | HTMLTextAreaElement,
  value: string,
) => {
  const prototype = input instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype
  const valueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set

  valueSetter?.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

export const closeDemoOptions = (element: HTMLElement) => {
  const input = getInput(element)
  const activeElement = document.activeElement
  const focusTarget = input || element

  focusTarget.dispatchEvent(new KeyboardEvent('keydown', {
    bubbles: true,
    code: 'Escape',
    key: 'Escape',
  }))
  focusTarget.dispatchEvent(new KeyboardEvent('keyup', {
    bubbles: true,
    code: 'Escape',
    key: 'Escape',
  }))
  input?.blur()
  element.blur()

  if (activeElement instanceof HTMLElement) {
    activeElement.blur()
  }
}

const typeField = async ({
  field,
  controls,
  visibleFields,
  shouldKeepFocus = false,
  shouldKeepCentered = true,
}: DemoPlaybackFieldParams) => {
  const input = getInput(field.element)

  if (!input) {
    return false
  }

  const animatedField = field.animatedField || prepareField(input, field.value)

  if (!animatedField) {
    return false
  }

  const isPreparedField = visibleFields.some(({ element }) => element === animatedField.element)

  if (isPreparedField) {
    controls.showFields([ ...visibleFields ])
  } else {
    controls.showFields([ ...visibleFields, animatedField ])
  }

  input.focus({ preventScroll: true })
  await waitForFieldLayout()
  if (controls.isCancelled()) return false
  centerDemoField(input)
  controls.setActiveFieldElement(input)

  for (let index = 1; index <= field.value.length; index += 1) {
    if (controls.isCancelled()) {
      restoreFields([ animatedField ])
      return false
    }

    await wait(0)

    const value = field.value.slice(0, index)

    field.onType?.(value)

    if (!field.onType && !field.animatedField) {
      setInputValue(input, value)
    }

    await waitForFieldLayout()

    if (controls.isCancelled()) {
      restoreFields([ animatedField ])
      return false
    }

    if (shouldKeepCentered || input.closest('.demo-player')) {
      centerDemoField(input)
    }
    await wait(0)
    animatedField.visibleValue = value
    refreshFieldOverlayStyle(animatedField)

    if (isPreparedField) {
      controls.showFields([ ...visibleFields ])
    } else {
      controls.showFields([ ...visibleFields, animatedField ])
    }

    await wait(DEMO_CHARACTER_DELAY)
  }

  controls.setActiveFieldElement(null)
  await waitForFieldLayout()
  if (!shouldKeepFocus) {
    input.blur()
  }

  if (!isPreparedField) {
    restoreFields([ animatedField ])
    controls.showFields(visibleFields)
  }

  return true
}

const chooseOption = async (
  field: DemoPlaybackField,
  controls: PlaybackControls,
) => {
  field.element.focus({ preventScroll: true })
  field.element.dispatchEvent(new MouseEvent('mousedown', {
    bubbles: true,
    cancelable: true,
    view: window,
  }))

  const optionSelector = field.optionSelector || `[role="option"][data-value="${field.value}"]`
  const option = await waitForDocumentElement(optionSelector, controls.isCancelled)

  if (!option) {
    return false
  }

  await animateAction(option, false)
  option.click()
  field.onChoose?.(field.value)
  closeDemoOptions(field.element)

  await wait(DEMO_STEP_DELAY)

  return true
}

const chooseAutocomplete = async ({
  field,
  controls,
  visibleFields,
  shouldKeepCentered = true,
}: DemoPlaybackFieldParams) => {
  const fieldIsTyped = await typeField({ field, controls, visibleFields, shouldKeepFocus: true, shouldKeepCentered })

  if (!fieldIsTyped || !field.optionSelector) {
    return false
  }

  const input = getInput(field.element)
  const listIsOpen = input?.getAttribute('aria-expanded') === 'true'
  const autocomplete = field.element.closest<HTMLElement>('.MuiAutocomplete-root')

  autocomplete?.setAttribute('data-demo-playback-action', 'autocomplete-option')

  if (!listIsOpen) {
    input?.dispatchEvent(new KeyboardEvent('keydown', {
      bubbles: true,
      code: 'ArrowDown',
      key: 'ArrowDown',
    }))
  }

  const option = await waitForDocumentElement(field.optionSelector, controls.isCancelled)

  if (!option) {
    autocomplete?.removeAttribute('data-demo-playback-action')
    return false
  }

  await animateAction(option, false)

  if (field.onChoose) {
    field.onChoose(field.value)
  } else {
    option.click()
  }

  if (field.animatedField) {
    field.animatedField.visibleValue = field.animatedField.targetValue
    controls.showFields(visibleFields)
  }

  closeDemoOptions(field.element)
  autocomplete?.removeAttribute('data-demo-playback-action')
  controls.setActiveFieldElement(null)
  await wait(DEMO_STEP_DELAY)

  return true
}

const chooseDate = async (
  field: DemoPlaybackField,
  controls: PlaybackControls,
) => {
  const openAction = field.element.querySelector<HTMLButtonElement>('button') || field.element

  await animateAction(field.element)
  controls.setActiveFieldElement(null)
  field.onOpen?.()
  openAction.click()

  const selectedDate = await waitForDocumentElement(
    field.optionSelector ||
    '.MuiPickersPopper-root button[aria-selected="true"], [role="dialog"] button[aria-selected="true"]',
    controls.isCancelled,
  )

  if (!selectedDate) {
    return false
  }

  await animateAction(selectedDate, false)
  selectedDate.click()
  field.onChoose?.(field.value)

  await wait(DEMO_STEP_DELAY)

  return true
}

const chooseRadio = async (field: DemoPlaybackField) => {
  const radio = field.element.querySelector<HTMLInputElement>(
    field.optionSelector || `input[value="${field.value}"]`,
  )

  if (!radio) {
    return false
  }

  const action = radio.closest<HTMLElement>('label') || radio

  await animateAction(action)
  radio.click()
  field.onChoose?.(field.value)

  return true
}

const playField = ({
  field,
  controls,
  visibleFields,
  shouldKeepCentered = true,
}: DemoPlaybackFieldParams) => {
  if (field.type === DEMO_FIELD_TYPE.TEXTFIELD) {
    return typeField({ field, controls, visibleFields, shouldKeepCentered })
  }

  if (field.type === DEMO_FIELD_TYPE.AUTOCOMPLETE) {
    return chooseAutocomplete({ field, controls, visibleFields, shouldKeepCentered })
  }

  if (field.type === DEMO_FIELD_TYPE.SELECT) {
    return chooseOption(field, controls)
  }

  if (field.type === DEMO_FIELD_TYPE.DATE) {
    return chooseDate(field, controls)
  }

  return chooseRadio(field)
}

export const playDemoFields = async ({
  controls,
  fields,
  step,
  visibleFields = [],
}: PlayDemoFieldsParams) => {
  const activeVisibleFields = visibleFields

  for (const field of fields) {
    const shouldScrollSmoothly = [ DEMO_CALCULATOR_STEP.CUSTOMS, DEMO_CALCULATOR_STEP.CERTIFICATION, DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS, DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES ].includes(step)
    const handledInsidePlayer = shouldScrollSmoothly
      ? await scrollToDemoPlayerElement({ element: field.element, isCancelled: controls.isCancelled })
      : centerDemoField(field.element)

    if (controls.isCancelled()) {
      return false
    }

    if (!handledInsidePlayer && field.shouldScroll !== false) {
      field.element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      await wait(DEMO_SCROLL_DELAY)
    }

    const fieldIsComplete = await playField({
      field,
      controls,
      visibleFields: activeVisibleFields,
      shouldKeepCentered: !handledInsidePlayer,
    })

    if (!fieldIsComplete) {
      return false
    }

  }

  return true
}
